/**
 * Map page — oilfield production markers + petrophysical atlas (provinces).
 */
import { Box, Container, Paper, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';

import { FieldsMapView } from '@/components/Map/FieldsMapView';
import { ProvincesMapView } from '@/components/Map/ProvincesMapView';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`map-tabpanel-${index}`}
      aria-labelledby={`map-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const MAP_TAB_LABELS = ['Oilfield production', 'Petrophysical atlas'] as const;

export const MapPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  useRegisterPageHelp('Map', MAP_TAB_LABELS[activeTab] ?? null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const mapHeight = 'calc(100vh - 280px)';

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Map
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          {activeTab === 0
            ? 'Explore fields on the map. Click a marker to open historical data.'
            : 'Browse oil provinces and their petrophysical attribute ranges. Available to all users.'}
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ mb: 1, bgcolor: 'transparent' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="map tabs"
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              minHeight: 40,
              borderRadius: 1,
              mx: 0.5,
              px: 2,
              py: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 400,
              border: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              '&:hover': { backgroundColor: 'action.hover' },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                borderColor: 'primary.main',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'primary.dark' },
              },
            },
          }}
        >
          <Tab
            label="Oilfield production"
            id="map-tab-0"
            aria-controls="map-tabpanel-0"
          />
          <Tab
            label="Petrophysical atlas"
            id="map-tab-1"
            aria-controls="map-tabpanel-1"
          />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <FieldsMapView mapHeight={mapHeight} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <ProvincesMapView mapHeight={mapHeight} />
      </TabPanel>
    </Container>
  );
};
